import * as Colors from '@brightlayer-ui/colors';
import { Tokens } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import OktaSignIn from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/ableedgedevportal.png';
import backgroundImage from '../../assets/background.svg';
import { Logger } from '../../logger';
import { getOktaConfig } from '../../oktaConfig';

import { Spinner } from '@brightlayer-ui/react-auth-workflow';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from '../../app/store';
import { setCredentialsAndUser } from '../../features/userSlice';
import { UserService } from '../../services/user/user.service';
import './Login.css';

// Patch for Okta widget ColorsUtil.addStyle null container issue
// This happens when the widget tries to add styles before its container is in the DOM
const patchOktaColorsUtil = (): void => {
    const originalGetElementById = document.getElementById.bind(document);
    document.getElementById = function (id: string): HTMLElement | null {
        const element = originalGetElementById(id);
        // If looking for okta-sign-in container and it doesn't exist,
        // return document.head as a fallback to prevent appendChild error
        if (!element && id === 'okta-sign-in') {
            Logger.warn(`Okta widget container '${id}' not found, using document.head as fallback`);
            return document.head;
        }
        return element;
    };
};

// Apply patch once when module loads
patchOktaColorsUtil();

type LoginProps = {
    onSuccessfulLogin: (tokens: Tokens) => Promise<void>;
};

export const Login: React.FC<LoginProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { oktaAuth } = useOktaAuth();
    const widgetRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState(true);

    // Extract state from URL if present (for OAuth flow)
    const state = new URLSearchParams(window.location.search).get('state') ?? undefined;

    // Check for existing authentication session on mount
    // This detects SSO conflicts early (e.g., user logged into Eaton Portal but not registered in AbleEdge)
    useEffect(() => {
        const checkExistingAuth = async (): Promise<void> => {
            try {
                const accessToken = await oktaAuth.tokenManager.get('accessToken');
                if (!accessToken || !('accessToken' in accessToken)) {
                    return; // No existing session
                }

                // Try to fetch user - will throw 404 if not registered
                try {
                    const user = await UserService.getUser(accessToken.accessToken);

                    // User exists and is registered, save to Redux and redirect to home
                    Logger.info('User already authenticated and registered, redirecting to home');
                    dispatch(setCredentialsAndUser({ user, credentials: { accessToken: accessToken.accessToken } }));
                    navigate('/');
                } catch (error) {
                    // Check if it's a 404 - user authenticated but not registered
                    if (error instanceof AxiosError && error.response?.status === 404) {
                        Logger.info('User authenticated in Okta but not registered. Clearing SSO session.');

                        // Clear Okta SSO session completely - allows user to register
                        await oktaAuth.signOut({
                            postLogoutRedirectUri: `${window.location.origin}/login`,
                        });
                        return; // signOut handles redirect
                    }
                }
            } catch (error) {
                // Ignore other errors - user likely not authenticated
                Logger.info(`Auth status check failed (expected if not authenticated): ${String(error)}`);
            }
        };

        void checkExistingAuth();
    }, [oktaAuth, navigate, dispatch]);

    useEffect(() => {
        if (!widgetRef.current) {
            Logger.error('Widget ref not available');
            return;
        }

        const { issuer, clientId, redirectUri, scopes } = getOktaConfig(window);

        /**
         * Note: when using the Sign-In Widget for an OIDC flow, it still
         * needs to be configured with the base URL for your Okta Org. Here
         * we derive it from the given issuer for convenience.
         */
        const widget = new OktaSignIn({
            baseUrl: issuer.split('/oauth2')[0],
            clientId,
            redirectUri,
            logo,
            i18n: {
                en: {
                    'primaryauth.username.placeholder': t('LOGIN_PAGE.EMAIL_ADDRESS'),
                    'oie.remember': t('LOGIN_PAGE.REMEMBER_ME'),
                    'oie.primaryauth.submit': t('LOGIN_PAGE.LOG_IN'),
                    forgotpassword: t('LOGIN_PAGE.FORGOT_PASSWORD'),
                },
            },
            authParams: {
                issuer,
                scopes,
            },
            useInteractionCodeFlow: true,
            state,
            colors: {
                brand: Colors.blue[500],
            },
            customButtons: [
                {
                    title: t('LOGIN_PAGE.CREATE_ACCOUNT'),
                    className: 'create-account-button',
                    click: (): void => {
                        navigate('/register/create-account');
                    },
                },
            ],
        });

        widget
            .renderEl(
                { el: widgetRef.current },
                (res) => {
                    if (res.status === 'SUCCESS') {
                        const tokens = res.tokens;
                        if (!tokens) {
                            Logger.error('No tokens received after successful login');
                            setIsLoading(false);
                            return;
                        }

                        // Handle successful login
                        try {
                            // Set tokens in Okta
                            oktaAuth.tokenManager.setTokens(tokens);
                            // Handle our login logic (which may be synchronous or asynchronous and may include navigation)
                            const loginResult = props.onSuccessfulLogin(tokens);
                            // Ensure any asynchronous errors are caught and handled
                            void Promise.resolve(loginResult).catch((error) => {
                                Logger.error(`Error handling successful login: ${String(error)}`);
                                setIsLoading(false);
                            });
                            // Note: Don't call widget.remove() here - let the useEffect cleanup handle it
                            // Calling remove() during render can cause ColorsUtil.addStyle to fail
                        } catch (error) {
                            Logger.error(`Error handling successful login: ${String(error)}`);
                            setIsLoading(false);
                        }
                    }
                },
                (err) => {
                    Logger.error(`Okta widget render error: ${err instanceof Error ? err.message : 'Unknown error'}`);
                    setIsLoading(false);
                }
            )
            .catch((error) => {
                Logger.error(`Error rendering Okta login widget: ${String(error)}`);
                setIsLoading(false);
            });

        // Apply page styling (must be done here due to Okta widget requirements)
        const documentRoot = document.documentElement;
        documentRoot.style.setProperty('--primary-color', Colors.blue[500]);
        document.body.style.backgroundColor = Colors.blue[500];
        document.body.style.backgroundImage = `url(${backgroundImage})`;

        // Hide loading spinner when widget is ready
        widget.on('afterRender', () => {
            setIsLoading(false);
        });

        // Cleanup function
        return (): void => {
            try {
                widget.remove();
            } catch (error) {
                Logger.error(`Error removing Okta widget: ${String(error)}`);
            }

            // Clear the page styling
            document.body.style.backgroundColor = '';
            document.body.style.backgroundImage = '';
        };
    }, [oktaAuth, navigate, props.onSuccessfulLogin, state, t]);

    return (
        <>
            <Spinner visible={isLoading} />
            <div ref={widgetRef} />
        </>
    );
};
